import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import reportService, { type FormatPreviewResponse, type ReportResponse } from '../../services/reportService';
import { getEffectiveReportingProfile, type ReportStyleSpec } from '../../services/rxfService';
import { reportTextToHtml } from '../../utils/reportUtils';
import ReportStyleBuilder, { DEFAULT_REPORT_STYLE } from './ReportStyleBuilder';

interface Props {
  open: boolean;
  report: ReportResponse;
  onClose: () => void;
  onApproved: (report: ReportResponse) => void;
}

const errorMessage = (error: unknown) => {
  const detail = (error as { response?: { data?: { detail?: string | Array<{ msg?: string }> } } }).response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((item) => item.msg).filter(Boolean).join('; ');
  return 'Unable to generate the formatting preview.';
};

const ReportFormatFeedbackDialog: React.FC<Props> = ({ open, report, onClose, onApproved }) => {
  const [style, setStyle] = useState<ReportStyleSpec>(DEFAULT_REPORT_STYLE);
  const [preview, setPreview] = useState<FormatPreviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setPreview(null);
    setError(null);
    getEffectiveReportingProfile()
      .then((profile) => setStyle({ ...DEFAULT_REPORT_STYLE, ...(profile?.style_spec || {}) }))
      .catch(() => setStyle(DEFAULT_REPORT_STYLE));
  }, [open]);

  const handlePreview = async () => {
    setLoading(true);
    setError(null);
    try {
      setPreview(await reportService.previewReportFormat(report.id, style));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!preview) return;
    setApproving(true);
    setError(null);
    try {
      const adopted = await reportService.approveReportFormatPreview(
        report.id,
        preview.style_spec,
        preview.preview_report_text,
        preview.receipt,
      );
      onApproved(adopted);
      onClose();
    } catch (err) {
      const message = errorMessage(err);
      setError(message.includes('mismatch') ? 'The report changed after this preview. Generate a fresh preview before approving.' : message);
    } finally {
      setApproving(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading || approving ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Improve report format</DialogTitle>
      <DialogContent dividers>
        {!preview ? (
          <ReportStyleBuilder value={style} onChange={setStyle} disabled={loading} error={error} />
        ) : (
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}
            <Alert severity={preview.pipeline_health === 'BLOCKED' ? 'error' : 'info'}>
              Pipeline health: {preview.pipeline_health}. Review the complete report before approval.
            </Alert>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>Original report</Typography>
                <Paper variant="outlined" sx={{ p: 2, maxHeight: '55vh', overflow: 'auto' }}>
                  <Box
                    data-testid="original-report-preview"
                    sx={{
                      '& p': { my: 0.75 },
                      '& strong': { fontWeight: 700 },
                      '& ol': { listStyleType: 'decimal', pl: 3, my: 0.5 },
                      '& ul': { listStyleType: 'disc', pl: 3, my: 0.5 },
                      '& li': { display: 'list-item', pl: 0.5 },
                    }}
                    dangerouslySetInnerHTML={{ __html: reportTextToHtml(preview.original_report_text) }}
                  />
                </Paper>
              </Box>
              <Box>
                <Typography variant="subtitle2" gutterBottom>Updated preview</Typography>
                <Paper variant="outlined" sx={{ p: 2, maxHeight: '55vh', overflow: 'auto' }}>
                  <Box
                    data-testid="updated-report-preview"
                    sx={{
                      '& p': { my: 0.75 },
                      '& strong': { fontWeight: 700 },
                      '& ol': { listStyleType: 'decimal', pl: 3, my: 0.5 },
                      '& ul': { listStyleType: 'disc', pl: 3, my: 0.5 },
                      '& li': { display: 'list-item', pl: 0.5 },
                    }}
                    dangerouslySetInnerHTML={{ __html: reportTextToHtml(preview.preview_report_text) }}
                  />
                </Paper>
              </Box>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading || approving}>Discard</Button>
        {preview ? (
          <>
            <Button onClick={() => { setPreview(null); setError(null); }} disabled={approving}>Revise guidance</Button>
            <Button variant="contained" onClick={handleApprove} disabled={approving || preview.pipeline_health === 'BLOCKED'}>
              {approving ? <CircularProgress size={18} /> : 'Approve & use for future reports'}
            </Button>
          </>
        ) : (
          <Button variant="contained" onClick={handlePreview} disabled={loading}>
            {loading ? <CircularProgress size={18} /> : 'Generate preview'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ReportFormatFeedbackDialog;
